import axios from 'axios';
import { memoize } from 'lodash';
import { IPerformanceSeatStatus } from '../classes/package-listing';

import { IPerformancePricing, IPackagePricing } from '../custom/classes/production';

export default class PackageApi {
    private static acePackageAPIRoute = process.env.PACKAGE_API_ROUTE;

    private static performancePricingCache = memoize(PackageApi.getPerformancePricingApi, (performanceId: number, packageId: number) => `perf-${performanceId} package-${packageId}`);

    private static packagePricingCache = memoize(PackageApi.getPackagePricingApi, (packageId: number) => `package-${packageId}`);

    private static filtersSource = axios.CancelToken.source();


    private static getPerformancePricingApi(performanceId: number, packageId: number): Promise<IPerformancePricing[]> {

        var requestString = `/${this.acePackageAPIRoute}${process.env.FLEX_PERFORMANCE_PRICING_ROUTE}?performanceId=${performanceId}&packageId=${packageId}`;

        return axios.get<IPerformancePricing[]>(requestString, {
            withCredentials: true,
            cancelToken: this.filtersSource.token
        }).then(response => response.data);
    }

    private static getPackagePricingApi(performanceId: number): Promise<IPackagePricing> {

        var requestString = `/${this.acePackageAPIRoute}${process.env.PACKAGE_PRICING_ROUTE}?packageId=${performanceId}`;

        return axios.get<IPerformancePricing>(requestString, {
            withCredentials: true,
            cancelToken: this.filtersSource.token
        }).then(response => response.data);
    }

    /**
     * Call the API to receive a more specific set of filters.
     * @param filter
     * @param culture
     */
    public static getPerformancePricing(performanceId: number, packageId: number): Promise<IPerformancePricing[]> {

        //return this.getPackagePricingApi(packageId, performanceIds);

        // This is the memoized variant. Since the request is cancelable, memoize would need to be tweaked to be properly utilized.
        return this.performancePricingCache(performanceId, packageId);
    }

    /**
     * Call the API to receive a more specific set of filters.
     * @param filter
     * @param culture
     */
    public static getPackagePricing(packageId: number): Promise<IPackagePricing> {

        // This is the memoized variant. Since the request is cancelable, memoize would need to be tweaked to be properly utilized.
        return this.packagePricingCache(packageId);
    }

    /**
     * Call this to clear the memoized events and start fresh.
     */
    public static clearPackagePricingCache(): void {
        return this.packagePricingCache.cache.clear();
    }

    /**
     * Call this to clear the memoized events and start fresh.
     */
    public static clearPerformancePricingCache(): void {
        return this.performancePricingCache.cache.clear();
    }

    public static processFlexPackage(packageId: number,
        performanceZones: { [key: number]: number; },
        requestedPriceTypeIds: Array<number>,
        isUnseated: boolean,
        orderNotes: string,
        configId: number,
        accessibleSeatingRequested: boolean,
        performanceSeatStatuses: Array<IPerformanceSeatStatus>) {

        var requestString = `/${this.acePackageAPIRoute}/ProcessPackageRequest`

        const packageRequest = {
            packageId,
            performanceZones,
            requestedPriceTypeIds,
            seatedRequest: !isUnseated,
            orderNotes,
            configId,
            accessibleSeatingRequested,
            performanceSeatStatuses
        };

        return axios.post(requestString, packageRequest,
            {
                method: "POST"
            }
        ).then(response => response.data);
    }

    public static processSuperPackage(packageId: number,
        subPackageZones: { [key: number]: number; },
        requestedPriceTypeIds: Array<number>,
        orderNotes: string,
        configId: number,
        accessibleSeatingRequested: boolean) {

        var requestString = `/${this.acePackageAPIRoute}/ProcessSuperPackageRequest`

        const packageRequest = {
            packageId,
            subPackageZones,
            requestedPriceTypeIds,
            orderNotes,
            configId,
            accessibleSeatingRequested
        };

        return axios.post(requestString, packageRequest,
            {
                method: "POST"
            }
        ).then(response => response.data);
    }

    public static processFixedPackage(packageId: number,
        zoneId: number,
        requestedPriceTypeIds: Array<number>,
        orderNotes: string,
        configId: number, seatingPreferences: string,
        accessibleSeatingRequested: boolean) {

        var requestString = `/${this.acePackageAPIRoute}/ProcessFixedPackageRequest`

        const packageRequest = {
            packageId,
            zoneId,
            requestedPriceTypeIds,
            orderNotes,
            configId,
            seatingPreferences,
            accessibleSeatingRequested
        };

        return axios.post(requestString, packageRequest,
            {
                method: "POST"
            }
        ).then(response => response.data);
    }

    public static flexPackageSessionVariable(packageId: number,
        performanceIds: string) {

        const requestString = `/${this.acePackageAPIRoute}/FlexPackageSessionVariable`

        const packageRequest = {
            packageId,
            performanceIds
        };

        return axios.post(requestString, packageRequest,
            {
                method: "POST"
            }
        ).then(response => response.data);
    }
}
